import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';

const steps = [
  { num: '01', title: 'Emergency Call', desc: 'Call our 24/7 hotline. A certified project manager answers immediately and dispatches the nearest crew.' },
  { num: '02', title: 'Rapid Deployment', desc: 'Our fully-equipped emergency response team arrives at your property within 60 minutes.' },
  { num: '03', title: 'Damage Assessment', desc: 'We perform a rapid but thorough assessment using moisture meters and thermal imaging to map all affected areas.' },
  { num: '04', title: 'Containment & Cleanup', desc: 'We contain the damage, extract water or debris, and begin drying and decontamination immediately.' },
  { num: '05', title: 'Documentation', desc: 'Detailed photo and written documentation is prepared for your insurance claim.' },
  { num: '06', title: 'Restoration Plan', desc: 'We present a complete restoration plan and timeline to return your property to pre-loss condition.' },
];

const emergencyTypes = [
  {
    icon: 'ri-drop-fill',
    title: 'Water & Flood Damage',
    desc: 'Burst pipes, appliance failures, flooding — we extract water and begin structural drying immediately.',
    img: 'https://public.readdy.ai/ai/img_res/a5d6a0089675edc8b58f7204924f3e90.jpg',
  },
  {
    icon: 'ri-fire-fill',
    title: 'Fire & Smoke Damage',
    desc: 'Post-fire cleanup including soot removal, smoke odor elimination, and structural stabilization.',
    img: 'https://readdy.ai/api/search-image?query=fire%20damaged%20room%20with%20smoke%20stains%20on%20walls%20and%20ceiling%2C%20post%20fire%20damage%20interior%2C%20dramatic%20scene%2C%20realistic&width=600&height=400&seq=em002&orientation=landscape',
  },
  {
    icon: 'ri-thunderstorms-fill',
    title: 'Storm & Wind Damage',
    desc: 'Emergency board-up, tarping, and debris removal after severe weather events.',
    img: 'https://readdy.ai/api/search-image?query=storm%20damaged%20roof%20and%20building%20exterior%20with%20debris%2C%20emergency%20tarping%20and%20board%20up%20service%2C%20dramatic%20weather%20scene&width=600&height=400&seq=em003&orientation=landscape',
  },
  {
    icon: 'ri-virus-fill',
    title: 'Biohazard Cleanup',
    desc: 'Safe, certified cleanup of biohazardous materials following strict EPA and OSHA protocols.',
    img: 'https://readdy.ai/api/search-image?query=biohazard%20cleanup%20team%20in%20full%20protective%20suits%20and%20equipment%2C%20professional%20decontamination%20service%2C%20clean%20professional%20environment&width=600&height=400&seq=em004&orientation=landscape',
  },
];

const emergencySchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: '24/7 Emergency Cleanup Services Houston TX',
    description: 'Immediate 24/7 emergency response for water, fire, storm, and biohazard damage. Certified crews arrive within 60 minutes to contain damage and begin cleanup.',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Mitigation Restoration Services',
      telephone: '+18005551234',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Houston',
        addressRegion: 'TX',
        addressCountry: 'US',
      },
    },
    areaServed: { '@type': 'City', name: 'Houston' },
    serviceType: 'Emergency Cleanup',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/emergency-cleanup`,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/` },
      { '@type': 'ListItem', position: 2, name: 'Services', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services` },
      { '@type': 'ListItem', position: 3, name: 'Emergency Cleanup', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/emergency-cleanup` },
    ],
  },
];

export default function EmergencyCleanupPage() {
  return (
    <div className="min-h-screen">
      <SeoHead
        title="24/7 Emergency Cleanup Services Houston TX | Mitigation Restoration"
        description="Immediate 24/7 emergency cleanup for water, fire, storm & biohazard damage in Houston TX. Certified crews on-site within 60 minutes. Call +1 (800) 555-1234 now."
        keywords="emergency cleanup Houston, 24/7 water damage cleanup, fire damage cleanup, storm damage cleanup, biohazard cleanup Houston TX"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/emergency-cleanup`}
        schema={emergencySchema}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=emergency%20restoration%20crew%20arriving%20at%20flooded%20property%20at%20night%20with%20floodlights%20and%20equipment%2C%20urgent%20response%20scene%2C%20dramatic%20lighting&width=1920&height=800&seq=emhero001&orientation=landscape"
            alt="Emergency Cleanup"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d1f35]/90 via-[#0d1f35]/70 to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-32 pb-20">
          <span className="inline-flex items-center gap-2 bg-[#DC2626] text-white text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-widest mb-5">
            <i className="ri-alarm-warning-fill"></i>
            24/7 Emergency Response
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Emergency Cleanup<br />Services
          </h1>
          <p className="text-white/80 text-base max-w-lg mb-8">
            When disaster strikes, every minute counts. Our certified emergency response teams are deployed within 60 minutes to contain damage and begin immediate cleanup.
          </p>
          <a
            href="tel:+18005551234"
            className="inline-flex items-center gap-2 bg-[#DC2626] text-white px-8 py-4 rounded-full font-semibold text-sm hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-fill"></i>
            Call Emergency Line Now
          </a>
        </div>
      </section>

      {/* Emergency Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-[#DC2626] text-xs font-semibold uppercase tracking-widest">We Handle</span>
            <h2 className="text-3xl font-extrabold text-[#1E3A5F] mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              All Types of Emergency Damage
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {emergencyTypes.map((type) => (
              <div key={type.title} className="rounded-2xl overflow-hidden group">
                <div className="relative h-48">
                  <img src={type.img} alt={type.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-4 left-4">
                    <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#DC2626] mb-2">
                      <i className={`${type.icon} text-white text-sm`}></i>
                    </div>
                    <h3 className="text-white font-bold text-sm">{type.title}</h3>
                  </div>
                </div>
                <div className="bg-[#F8F9FA] p-4">
                  <p className="text-gray-500 text-xs leading-relaxed">{type.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <span className="text-[#DC2626] text-xs font-semibold uppercase tracking-widest">Our Process</span>
            <h2 className="text-3xl font-extrabold text-[#1E3A5F] mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              How We Respond to Emergencies
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {steps.map((step) => (
              <div key={step.num} className="bg-white rounded-2xl p-6">
                <p className="text-5xl font-extrabold text-[#DC2626]/20 mb-3">{step.num}</p>
                <h3 className="font-bold text-[#1E3A5F] text-base mb-2">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#DC2626] text-center">
        <div className="max-w-2xl mx-auto px-4">
          <i className="ri-alarm-warning-fill text-white text-4xl mb-4 block"></i>
          <h3 className="text-2xl font-extrabold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Experiencing an Emergency Right Now?
          </h3>
          <p className="text-white/80 text-sm mb-6">Don&apos;t wait — call us immediately. Every minute of delay increases damage and restoration costs.</p>
          <a
            href="tel:+18005551234"
            className="inline-flex items-center justify-center gap-2 bg-white text-[#DC2626] px-10 py-4 rounded-full font-bold text-base hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-phone-fill"></i>
            +1 (800) 555-1234
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
